import { Content, Header, InfoCard, Page } from "@backstage/core-components";
import { configApiRef, useApi } from "@backstage/core-plugin-api";
import { Box, Button, Grid, Typography, makeStyles } from "@material-ui/core";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: "calc(100vh - 180px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    width: "100%",
    maxWidth: 480,
    textAlign: "center",
  },
  iconWrapper: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 64,
    height: 64,
    borderRadius: "50%",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.04)",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  icon: {
    fontSize: 32,
  },
  message: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    lineHeight: 1.6,
  },
  button: {
    minWidth: 160,
    padding: theme.spacing(1, 3),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "1rem",
  },
}));

export interface SessionExpiredScreenProps {
  error?: Error;
  onContinue?: () => void;
}

/**
 * Pantalla informativa accesible para ProxiedSignInPage cuando no se puede
 * validar la sesión corporativa de Azure AD / Entra Application Proxy en el arranque.
 * Sustituye errores técnicos por un flujo guiado con reautenticación top-level.
 */
export const SessionExpiredScreen = ({
  onContinue,
}: SessionExpiredScreenProps) => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const appTitle = configApi.getOptionalString("app.title") ?? "Backstage";

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
      return;
    }

    if (typeof window !== "undefined" && window.location) {
      if (typeof window.location.assign === "function") {
        window.location.assign(window.location.href);
      } else {
        window.location.replace(window.location.href);
      }
    }
  };

  return (
    <Page themeId="home">
      <Header title={appTitle} type="Autenticación" />
      <Content>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          className={classes.container}
        >
          <Grid item xs={12} sm={8} md={6} lg={4}>
            <InfoCard
              title="No se pudo validar la sesión"
              titleTypographyProps={{
                component: "h2",
                variant: "h5",
                align: "center",
              }}
              className={classes.card}
              noPadding
            >
              <Box
                p={3}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <div className={classes.iconWrapper} aria-hidden="true">
                  <LockOutlinedIcon className={classes.icon} />
                </div>
                <Typography
                  variant="body1"
                  className={classes.message}
                  align="center"
                >
                  No ha sido posible validar tu sesión corporativa. Pulsa en
                  Continuar para iniciar sesión a través del portal de
                  autenticación.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleContinue}
                  className={classes.button}
                >
                  Continuar
                </Button>
              </Box>
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
